import type {
  AriaAttributes,
  ChangeEvent,
  FocusEvent,
  MouseEvent,
  ReactElement,
  TouchEvent,
} from 'react';

export enum Size {
  BIG = 'big',
  MEDIUM = 'medium',
  SMALL = 'small',
}

export enum Status {
  DEFAULT = 'default',
  ERROR = 'error',
  SUCCESS = 'success',
}

export enum BorderRadiusSize {
  NONE = 'none',
  SMALL = 'small',
  MEDIUM = 'medium',
  BIG = 'big',
}

export interface BaseInputProps
  extends Pick<
    AriaAttributes,
    'aria-label' | 'aria-controls' | 'aria-expanded' | 'aria-haspopup'
  > {
  /**
   * Sets a unique id for the HTML element
   */
  id?: string;
  /**
   * Input size. Sets the size. Can be one of: Input.Size.SMALL, Input.Size.MEDIUM, Input.Size.BIG
   */
  size?: Size;
  /**
   * A text placeholder showed into input
   */
  placeholder: string;
  /**
   * Information displayed below the input
   */
  info?: string;
  /**
   * Input status. Can be one of:
   * Input.Status.DEFAULT
   * Input.Status.ERROR
   * Input.Status.SUCCESS
   */
  status?: Status;
  /**
   * Tells whether the input is disabled or not
   */
  disabled?: boolean;
  /**
   * If true, prevents the value of the input from being edited.
   */
  readOnly?: boolean;
  /**
   * Property used for tests purpose
   */
  dataCy?: string;
  /**
   * Property used for forms
   */
  name?: string;
  /**
   * Input value can be passed through props
   */
  value?: string | number;
  /**
   * Sets maximum width of input
   */
  maxLength?: number;
  RightIcon?: ReactElement;
  /**
   * Pressing Enter makes input loses focus
   */
  blurOnEnter?: boolean;
  /**
   * Sets minimum width of input
   */
  minLength?: number;
  /**
   * The input gets focus automatically
   */
  autofocus?: boolean;
  /**
   * Function for input change
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onRightIconClick?: (e: MouseEvent<HTMLInputElement>) => void;
  /**
   * Input loses focus
   */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /**
   * Input gets focus
   */
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  /**
   * Specifies whether or not an input field should have autocomplete enabled
   */
  autoComplete?: string;
  /**
   * Function that is executed when you tap on the item.
   */
  onTouchStart?: (event: TouchEvent) => void;

  /**
   * Force font size of input
   * In some cases is necesary to force the font size of the input to avoid zoom in Safari on iPhone
   *
   * https://stackoverflow.com/questions/2989263/disable-auto-zoom-in-input-text-tag-safari-on-iphone
   */
  forceFontSize?: string;

  /**
   * Lets you customize  the role of the input element.
   */
  role?: string;
}
